import React, { useState, useRef, useEffect } from "react";
import {
  Sparkles,
  FolderOpen,
  Keyboard,
  SlidersHorizontal,
  Calendar,
  ArrowRight,
  CalendarDays,
  Clock,
  Pen,
} from "lucide-react";
import { parseDateFromTitle } from "../../lib/utils";

interface WelcomeEmptyStateProps {
  onCreateTask: () => void;
  onQuickAdd: (title: string) => void;
}

const EXAMPLE_TASKS = [
  "Buy groceries tomorrow",
  "Team standup Monday at 10am",
  "Dentist appointment Friday at 3pm",
  "Finish quarterly report",
];

const tips = [
  {
    icon: Sparkles,
    title: "Smart date parsing",
    desc: "Type naturally — dates and times are extracted from your title automatically",
  },
  {
    icon: FolderOpen,
    title: "Organize by list",
    desc: "Group tasks into columns — create lists in Settings",
  },
  {
    icon: SlidersHorizontal,
    title: "Custom filter views",
    desc: "Save filters by priority, list, or tag for quick switching",
  },
  {
    icon: Calendar,
    title: "Calendar integration",
    desc: "Connect Google Calendar — scheduled tasks auto-book as events",
  },
  {
    icon: Keyboard,
    title: "Keyboard-first",
    desc: "N new task · / search · ? all shortcuts",
  },
];

const WelcomeEmptyState: React.FC<WelcomeEmptyStateProps> = ({
  onCreateTask,
  onQuickAdd,
}) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Live parse preview
  const parsed = inputValue.trim()
    ? parseDateFromTitle(inputValue)
    : null;
  const hasDate = parsed?.date != null;
  const hasTime = parsed?.time != null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    onQuickAdd(inputValue);
    setInputValue("");
  };

  const handleChipClick = (example: string) => {
    setInputValue(example);
    inputRef.current?.focus();
  };

  // Auto-focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-col items-center py-10 px-6 max-w-lg mx-auto animate-[fadeIn_0.5s_ease-out]">
      {/* Animated panoramic columns illustration */}
      <div className="relative w-20 h-20 mb-6">
        <svg
          viewBox="0 0 80 80"
          className="w-full h-full"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {/* Column 1 */}
          <rect
            x="8" y="20" width="16" height="44" rx="3"
            className="fill-warm-blue/10 stroke-warm-blue"
            strokeWidth="1.5"
            style={{ animation: "growUp 0.8s ease-out 0.2s both" }}
          />
          {/* Lines in column 1 */}
          <line x1="13" y1="30" x2="19" y2="30" className="stroke-warm-blue/40" strokeWidth="1.5" style={{ animation: "fadeIn 0.4s ease-out 0.9s both" }} />
          <line x1="13" y1="36" x2="21" y2="36" className="stroke-warm-blue/40" strokeWidth="1.5" style={{ animation: "fadeIn 0.4s ease-out 1.0s both" }} />
          <line x1="13" y1="42" x2="18" y2="42" className="stroke-warm-blue/40" strokeWidth="1.5" style={{ animation: "fadeIn 0.4s ease-out 1.1s both" }} />

          {/* Column 2 */}
          <rect
            x="32" y="28" width="16" height="36" rx="3"
            className="fill-warm-blue/10 stroke-warm-blue"
            strokeWidth="1.5"
            style={{ animation: "growUp 0.8s ease-out 0.4s both" }}
          />
          {/* Lines in column 2 */}
          <line x1="37" y1="38" x2="43" y2="38" className="stroke-warm-blue/40" strokeWidth="1.5" style={{ animation: "fadeIn 0.4s ease-out 1.1s both" }} />
          <line x1="37" y1="44" x2="45" y2="44" className="stroke-warm-blue/40" strokeWidth="1.5" style={{ animation: "fadeIn 0.4s ease-out 1.2s both" }} />

          {/* Column 3 */}
          <rect
            x="56" y="24" width="16" height="40" rx="3"
            className="fill-warm-blue/10 stroke-warm-blue"
            strokeWidth="1.5"
            style={{ animation: "growUp 0.8s ease-out 0.6s both" }}
          />
          {/* Lines in column 3 */}
          <line x1="61" y1="34" x2="67" y2="34" className="stroke-warm-blue/40" strokeWidth="1.5" style={{ animation: "fadeIn 0.4s ease-out 1.2s both" }} />
          <line x1="61" y1="40" x2="69" y2="40" className="stroke-warm-blue/40" strokeWidth="1.5" style={{ animation: "fadeIn 0.4s ease-out 1.3s both" }} />
          <line x1="61" y1="46" x2="65" y2="46" className="stroke-warm-blue/40" strokeWidth="1.5" style={{ animation: "fadeIn 0.4s ease-out 1.4s both" }} />
        </svg>
      </div>

      {/* Heading */}
      <h2
        className="font-heading text-3xl text-warm-text-primary mb-2 italic"
        style={{ animation: "fadeIn 0.5s ease-out 0.3s both" }}
      >
        Welcome to JuDI
      </h2>
      <p
        className="text-sm text-warm-text-secondary leading-relaxed mb-8 text-center max-w-sm"
        style={{ animation: "fadeIn 0.5s ease-out 0.5s both" }}
      >
        A panoramic task board — see everything at once, organized in columns by
        list.
      </p>

      {/* Inline task input */}
      <form
        onSubmit={handleSubmit}
        className="w-full mb-2"
        style={{ animation: "fadeIn 0.5s ease-out 0.6s both" }}
      >
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="What needs to be done?"
            className="w-full pl-4 pr-12 py-3 text-sm bg-warm-bg-card border border-warm-border rounded-card shadow-soft-sm focus:border-warm-blue focus:ring-1 focus:ring-warm-blue/30 text-warm-text-primary placeholder:text-warm-text-tertiary outline-none transition-all"
          />
          <button
            type="submit"
            disabled={!inputValue.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-soft bg-warm-blue text-white disabled:opacity-30 disabled:cursor-default hover:bg-warm-blue-dark transition-colors"
            aria-label="Create task"
          >
            <ArrowRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      </form>

      {/* Live parse preview */}
      <div
        className="w-full h-6 mb-5 flex items-center justify-center gap-3 text-xs"
        style={{ animation: "fadeIn 0.5s ease-out 0.6s both" }}
      >
        {parsed && (hasDate || hasTime) ? (
          <>
            {hasDate && (
              <span className="inline-flex items-center gap-1 text-warm-blue">
                <CalendarDays size={12} />
                {formatDatePreview(parsed.date!)}
              </span>
            )}
            {hasTime && (
              <span className="inline-flex items-center gap-1 text-warm-blue">
                <Clock size={12} />
                {formatTimePreview(parsed.time!)}
              </span>
            )}
            {parsed.title !== inputValue && (
              <span className="text-warm-text-tertiary">
                → &ldquo;{parsed.title.trim()}&rdquo;
              </span>
            )}
          </>
        ) : inputValue.trim() ? (
          <span className="text-warm-text-tertiary">
            Tip: try adding a date — "tomorrow", "Monday at 2pm"
          </span>
        ) : null}
      </div>

      {/* Example task chips */}
      <div
        className="flex flex-wrap justify-center gap-2 mb-8"
        style={{ animation: "fadeIn 0.5s ease-out 0.8s both" }}
      >
        <span className="text-xs text-warm-text-tertiary mr-1 self-center">Try:</span>
        {EXAMPLE_TASKS.map((example) => (
          <button
            key={example}
            onClick={() => handleChipClick(example)}
            className="px-2.5 py-1 text-xs rounded-full border border-warm-border bg-warm-bg-card text-warm-text-secondary hover:border-warm-blue hover:text-warm-blue transition-colors"
          >
            {example}
          </button>
        ))}
      </div>

      {/* Divider with "or" */}
      <div
        className="w-full flex items-center gap-3 mb-6"
        style={{ animation: "fadeIn 0.5s ease-out 0.9s both" }}
      >
        <div className="flex-1 h-px bg-warm-border" />
        <span className="text-xs text-warm-text-tertiary">or use the full editor</span>
        <div className="flex-1 h-px bg-warm-border" />
      </div>

      {/* Full editor CTA */}
      <button
        onClick={onCreateTask}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-warm-text-secondary border border-warm-border rounded-card hover:border-warm-blue hover:text-warm-blue bg-warm-bg-card transition-colors mb-10"
        style={{ animation: "fadeIn 0.5s ease-out 1.0s both" }}
      >
        <Pen size={14} />
        Open task editor
        <kbd className="ml-1 px-1.5 py-0.5 text-[10px] bg-warm-bg-elevated border border-warm-border rounded font-mono text-warm-text-tertiary leading-none">
          N
        </kbd>
      </button>

      {/* Feature tips — staggered */}
      <div className="w-full grid gap-2 text-left">
        {tips.map((tip, i) => (
          <div
            key={tip.title}
            className="flex items-start gap-3 px-3 py-2.5 rounded-card bg-warm-bg-card border border-warm-border"
            style={{ animation: `slideUp 0.4s ease-out ${1.1 + i * 0.1}s both` }}
          >
            <tip.icon size={14} className="text-warm-blue mt-0.5 shrink-0" />
            <div className="min-w-0">
              <span className="text-xs font-semibold text-warm-text-primary">{tip.title}</span>
              <span className="text-xs text-warm-text-tertiary ml-1.5">{tip.desc}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Keyframe styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes growUp {
          from { transform: scaleY(0); transform-origin: bottom; opacity: 0; }
          to { transform: scaleY(1); transform-origin: bottom; opacity: 1; }
        }
      `}</style>
    </div>
  );
};

/** Format "2026-03-15" → "Mar 15" */
function formatDatePreview(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Format "15:00" → "3:00 PM" */
function formatTimePreview(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${m.toString().padStart(2, "0")} ${suffix}`;
}

export default WelcomeEmptyState;
